import { useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getVisitDetails } from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_VISITS,
    SOCIETY_PORTAL_VISITORS_VIEW,
} from '../../../../Routing/routes';
import VisitorAvatar from '../components/VisitorAvatar';
import VmsBadge from '../components/vms/VmsBadge';
import { VmsLoader } from '../components/vms/VmsListShell';
import VisitorNotFound from '../components/VisitorNotFound';
import useVisitTransitions from './useVisitTransitions';
import VisitTransitionDialogs from './components/VisitTransitionDialogs';
import { availableActions, canCancelVisit } from './visitActions';
import {
    flatLabel,
    VISIT_STATUS_STYLE,
    VISIT_STATUS_ICON,
    PURPOSE_ICON,
    fmtDateTime,
    humanize,
    personName,
} from '../vmsConstants';
import { fmtPhone, typeLabel } from '../visitorConstants';

const Cell = ({ label, icon, children, className = '' }) => (
    <div className={className}>
        <p className="tw-mb-0 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-900 tw-font-bold">{label}</p>
        <div className="tw-text-xs tw-text-gray-800 tw-mt-1 tw-flex tw-items-center tw-gap-1.5">
            {icon && <Icon icon={icon} className="tw-text-gray-400 tw-shrink-0" />}
            {children}
        </div>
    </div>
);

const None = () => <span className="tw-text-gray-400">—</span>;

const ViewVisit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const canUpdate = can(PERMISSIONS.VMS_VISITOR_UPDATE);
    const canDelete = can(PERMISSIONS.VMS_VISITOR_DELETE);

    const { details: visit, detailsLoading } = useSelector(state => state.VmsReducer?.visits) || {};

    useEffect(() => {
        if (id) dispatch(getVisitDetails({ id }));
    }, [dispatch, id]);

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITS);
    const tx = useVisitTransitions(() => dispatch(getVisitDetails({ id })));

    const shell = (children) => (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">{children}</div>
    );

    if (detailsLoading) return shell(<div className="tw-py-16"><VmsLoader label="Loading visit..." /></div>);
    if (!visit) return shell(<VisitorNotFound onBack={backToList} />);

    const actions = availableActions(visit);
    const visitor = visit.visitor || {};

    return shell(
        <div className="tw-w-full tw-space-y-4">
            <button
                onClick={backToList}
                className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
            >
                <Icon icon="mdi:arrow-left" /> Back to Visits
            </button>

            {/* A PENDING_OTP visit is the one case where the operator has to do
                something the API cannot do for them: read the code out. Nothing
                delivers it to the visitor. */}
            {visit.status === 'PENDING_OTP' && canUpdate && (
                <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-2 tw-px-4 tw-py-3 tw-rounded-xl tw-bg-violet-50 tw-border tw-border-violet-200 tw-text-violet-900">
                    <Icon icon="mdi:shield-key-outline" className="tw-text-lg tw-shrink-0" />
                    <div className="tw-flex-1 tw-min-w-[220px]">
                        <p className="tw-mb-0 tw-text-xs tw-font-bold">Waiting on OTP verification</p>
                        <p className="tw-mb-0 tw-text-[11px] tw-mt-0.5">
                            The code is not sent to the visitor by SMS or app — open the dialog to read it out at the gate.
                        </p>
                    </div>
                    <button
                        onClick={() => tx.open('verifyOtp', visit)}
                        disabled={tx.acting}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-violet-600 hover:tw-bg-violet-700 tw-rounded-md disabled:tw-opacity-50"
                    >
                        <Icon icon="mdi:shield-check-outline" className="tw-text-sm" />
                        Verify OTP
                    </button>
                </div>
            )}

            {visit.status === 'REJECTED' && (
                <div className="tw-flex tw-items-start tw-gap-2 tw-px-4 tw-py-3 tw-rounded-xl tw-bg-rose-50 tw-border tw-border-rose-200 tw-text-rose-800">
                    <Icon icon="mdi:close-octagon-outline" className="tw-text-lg tw-shrink-0" />
                    <div>
                        <p className="tw-mb-0 tw-text-xs tw-font-bold">Visit rejected</p>
                        <p className="tw-mb-0 tw-text-[11px] tw-mt-0.5">{visit.rejection_reason || 'No reason was recorded.'}</p>
                        <p className="tw-mb-0 tw-text-[10px] tw-text-rose-600 tw-mt-1">{fmtDateTime(visit.rejected_at)}</p>
                    </div>
                </div>
            )}

            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-p-5 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
                    <div className="tw-flex tw-items-center tw-gap-3.5 tw-min-w-0">
                        <VisitorAvatar visitor={visitor} size={64} />
                        <div className="tw-min-w-0">
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                <h1 className="tw-mb-0 tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-snug">
                                    {visitor.name || visit.visitor_name || 'Visitor'}
                                </h1>
                                <VmsBadge value={visit.status} styles={VISIT_STATUS_STYLE} icons={VISIT_STATUS_ICON} />
                            </div>
                            <p className="tw-mb-0 tw-text-xs tw-text-gray-500 tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
                                <span className="tw-font-mono tw-font-semibold tw-text-gray-700">{visit.visit_code}</span>
                                {visitor.phone && (
                                    <a href={`tel:${visitor.phone}`} className="tw-flex tw-items-center tw-gap-1 tw-font-mono hover:tw-text-sky-700">
                                        <Icon icon="mdi:phone-outline" className="tw-text-gray-400" />
                                        {fmtPhone(visitor.phone)}
                                    </a>
                                )}
                                {visitor.visitor_id && (
                                    <button
                                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS_VIEW.replace(':id', visitor.visitor_id))}
                                        className="tw-flex tw-items-center tw-gap-1 tw-text-sky-600 hover:tw-text-sky-700"
                                    >
                                        <Icon icon="mdi:account-details-outline" />
                                        Visitor profile
                                    </button>
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        {canUpdate && actions.map(a => (
                            <button
                                key={a.key}
                                onClick={() => tx.run(a.key, visit)}
                                disabled={tx.acting}
                                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-rounded-md tw-shadow-sm disabled:tw-opacity-50 ${a.solid}`}
                            >
                                <Icon icon={a.icon} className="tw-text-base" />
                                {a.label}
                            </button>
                        ))}
                        {/* Outlined rather than solid: cancelling is the
                            destructive fallback, not the expected next step. It
                            still carries its label and matches the height of the
                            buttons beside it — a bare icon read as unfinished. */}
                        {canDelete && canCancelVisit(visit) && (
                            <button
                                onClick={() => tx.open('cancel', visit)}
                                disabled={tx.acting}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-rose-700 tw-bg-white tw-border tw-border-rose-300 tw-rounded-md hover:tw-bg-rose-50 disabled:tw-opacity-50"
                            >
                                <Icon icon="mdi:cancel" className="tw-text-base" />
                                Cancel Visit
                            </button>
                        )}
                    </div>
                </div>

                <div className="tw-px-5 tw-py-3 tw-bg-gray-50/60 tw-border-b tw-border-gray-100 tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3">
                    <Cell label="Flat" icon="mdi:home-outline">
                        {flatLabel(visit) === '—' ? <None /> : flatLabel(visit)}
                    </Cell>
                    <Cell label="Purpose" icon={PURPOSE_ICON[visit.purpose] || PURPOSE_ICON.OTHER}>
                        {humanize(visit.purpose)}
                    </Cell>
                    <Cell label="Visitor type" icon="mdi:account-outline">
                        {visitor.visitor_type ? typeLabel(visitor.visitor_type) : <None />}
                    </Cell>
                    <Cell label="Companions" icon="mdi:account-multiple-outline">
                        {visit.companion_count ?? 0}
                    </Cell>
                </div>

                {/* Timeline of what actually happened, in the order it happened. */}
                <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                    <Cell label="Logged" icon="mdi:calendar-plus">{fmtDateTime(visit.created_at)}</Cell>
                    <Cell label="Expected" icon="mdi:clock-outline">
                        {visit.expected_at ? fmtDateTime(visit.expected_at) : <None />}
                    </Cell>
                    <Cell label="Approved" icon="mdi:check-decagram-outline">
                        {visit.approved_at ? fmtDateTime(visit.approved_at) : <None />}
                    </Cell>
                    <Cell label="Checked in" icon="mdi:login">
                        {visit.check_in_at
                            ? <span>{fmtDateTime(visit.check_in_at)}{visit.entry_gate ? ` · ${visit.entry_gate}` : ''}</span>
                            : <None />}
                    </Cell>
                    <Cell label="Checked out" icon="mdi:logout">
                        {visit.check_out_at
                            ? <span>{fmtDateTime(visit.check_out_at)}{visit.exit_gate ? ` · ${visit.exit_gate}` : ''}</span>
                            : <None />}
                    </Cell>
                    <Cell label="OTP verified" icon="mdi:shield-check-outline">
                        {visit.otp_verified_at ? fmtDateTime(visit.otp_verified_at) : <None />}
                    </Cell>
                </div>

                <div className="tw-p-5 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                    <Cell label="Vehicle" icon="mdi:car-outline">
                        {visit.vehicle_number
                            ? <span className="tw-font-mono">{visit.vehicle_number}{visit.vehicle_type ? ` · ${humanize(visit.vehicle_type)}` : ''}</span>
                            : <None />}
                    </Cell>
                    <Cell label="Company" icon="mdi:domain">{visitor.company_name || <None />}</Cell>
                    <Cell label="Purpose detail" icon="mdi:text-short">{visit.purpose_detail || <None />}</Cell>

                    {/* Why this visit was auto-approved, when it was. */}
                    {visit.preApproval && (
                        <Cell label="Matched pre-approval" icon="mdi:calendar-check-outline" className="sm:tw-col-span-2">
                            <span>
                                {visit.preApproval.visitor_name} · valid to {fmtDateTime(visit.preApproval.valid_until)}
                                {visit.preApproval.verification_mode === 'OTP' ? ' · OTP required' : ''}
                            </span>
                        </Cell>
                    )}
                    {visit.gatePass && (
                        <Cell label="Matched gate pass" icon="mdi:card-account-details-outline" className="sm:tw-col-span-2">
                            <span className="tw-font-mono">{visit.gatePass.pass_code}</span>
                        </Cell>
                    )}

                    {visit.remarks && (
                        <Cell label="Remarks" icon="mdi:note-text-outline" className="sm:tw-col-span-3">
                            <span className="tw-whitespace-pre-wrap">{visit.remarks}</span>
                        </Cell>
                    )}
                </div>

                <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50/40 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-text-[10px] tw-text-gray-500">
                    <span className="tw-flex tw-items-center tw-gap-1">
                        <Icon icon="mdi:identifier" className="tw-text-gray-400" />
                        Visit {visit.visit_id}
                    </span>
                    {visit.approved_by && (
                        <span className="tw-flex tw-items-center tw-gap-1">
                            <Icon icon="mdi:account-check-outline" className="tw-text-gray-400" />
                            Approved by {personName(visit.approvedByUser) !== '—' ? personName(visit.approvedByUser) : `user #${visit.approved_by}`}
                        </span>
                    )}
                    <span className="tw-flex tw-items-center tw-gap-1">
                        <Icon icon="mdi:clock-edit-outline" className="tw-text-gray-400" />
                        Updated {fmtDateTime(visit.updated_at)}
                    </span>
                </div>
            </div>

            <VisitTransitionDialogs tx={tx} onCancelled={backToList} />
        </div>
    );
};

export default ViewVisit;
